import React, { useState, useEffect } from 'react';
import { Link } from 'react-router-dom';
import { 
  collection, 
  query, 
  where, 
  orderBy, 
  getDocs,
  updateDoc,
  doc,
  serverTimestamp 
} from 'firebase/firestore';
import { db } from '../../firebase/config';
import { useAuth } from '../../context/AuthContext';
import { 
  Search, 
  Plus, 
  Edit, 
  Trash2, 
  Eye,
  Lock
} from 'lucide-react';
import TimelineDelayService from '../../services/TimelineDelayService';

const DossiersList = () => {
  const { user } = useAuth();
  const [dossiers, setDossiers] = useState([]);
  const [loading, setLoading] = useState(true);
  const [searchTerm, setSearchTerm] = useState('');

  // Fonction pour récupérer les dossiers
  const fetchDossiers = async () => {
    try {
      setLoading(true);
      
      const allDossiersQuery = query(
        collection(db, 'dossiers'),
        orderBy('createdAt', 'desc')
      );
      
      const dossiersSnapshot = await getDocs(allDossiersQuery);
      const dossiersData = dossiersSnapshot.docs.map(doc => ({ 
        id: doc.id, 
        ...doc.data() 
      }));
      
      // Filtrer les dossiers actifs (non fermés)
      const activeData = dossiersData.filter(d => d.status !== 'closed');
      setDossiers(activeData);
      
    } catch (error) {
      console.error('Erreur lors de la récupération des dossiers:', error);
    } finally {
      setLoading(false);
    }
  };

  // ✅ VÉRIFICATION AUTOMATIQUE DES RETARDS AU CHARGEMENT
  const checkDelaysAutomatically = async () => {
    try {
      // Vérifier automatiquement les retards en arrière-plan
      await TimelineDelayService.checkAllDossierDelays();
      // Recharger les dossiers pour afficher les nouveaux statuts
      await fetchDossiers();
    } catch (error) {
      console.error('Erreur lors de la vérification automatique des retards:', error);
    }
  };

  useEffect(() => {
    if (user) {
      fetchDossiers().then(() => {
        // Lancer la vérification automatique après le chargement initial
        checkDelaysAutomatically();
      });
    }
  }, [user]);

  // Fonction pour supprimer un dossier
  const handleDelete = async (dossierId) => {
    if (window.confirm('Êtes-vous sûr de vouloir supprimer ce dossier ?')) {
      try {
        // Logique de suppression ici
        console.log('Suppression du dossier:', dossierId);
        await fetchDossiers(); // Recharger la liste
      } catch (error) {
        console.error('Erreur lors de la suppression:', error);
      }
    }
  };

  // Fonction pour clôturer un dossier
  const handleCloseDossier = async (dossierId, clientName) => {
    if (window.confirm(`Êtes-vous sûr de vouloir clôturer le dossier de ${clientName} ?`)) {
      try {
        const dossierRef = doc(db, 'dossiers', dossierId);
        await updateDoc(dossierRef, {
          status: 'closed',
          closedAt: serverTimestamp(),
          closedBy: user?.uid
        });
        
        console.log('✅ Dossier clôturé:', dossierId);
        await fetchDossiers(); // Recharger la liste
        
      } catch (error) {
        console.error('❌ Erreur lors de la clôture:', error);
        alert('Erreur lors de la clôture du dossier');
      }
    }
  };

  // ✅ FONCTIONS POUR CALCULER LES BARRES DE PROGRESSION
  const getDefaultPhases = () => {
    const defaultChargeAffairePhases = [
      { name: "Dossier signé", date: "", status: "Non réalisé" },
      { name: "Facture d'acompte N°1 transmise au client", date: "", status: "Non réalisé" },
      { name: "Acompte N°1 payé", date: "", status: "Non réalisé" },
      { name: "Réalisation de la déclaration préalable", date: "", status: "Non réalisé" },
      { name: "Transmission du pouvoir Enedis au client", date: "", status: "Non réalisé" },
      { name: "Autorisation DP reçue", date: "", status: "Non réalisé" },
      { name: "Pouvoir Enedis reçu", date: "", status: "Non réalisé" }
    ];

    const defaultChefProjetPhases = [
      { name: "Réception des informations du chargé d'affaire", date: "", status: "Non réalisé" },
      { name: "Organisation de la visite technique", date: "", status: "Non réalisé" },
      { name: "Relevés des passages de câbles", date: "", status: "Non réalisé" },
      { name: "Réalisation des plans de chaînes", date: "", status: "Non réalisé" },
      { name: "Réalisation des calculs et calepinages des structures", date: "", status: "Non réalisé" },
      { name: "Lister le matériel nécessaire", date: "", status: "Non réalisé" },
      { name: "Demande de raccordement Enedis", date: "", status: "Non réalisé" },
      { name: "Demande de devis à au moins 2 fournisseurs", date: "", status: "Non réalisé" },
      { name: "Réception et analyse des offres", date: "", status: "Non réalisé" },
      { name: "Transmission de la facture de situation N°2", date: "", status: "Non réalisé" },
      { name: "Commande du matériel avec délai de réception", date: "", status: "Non réalisé" },
      { name: "Communication du planning au client", date: "", status: "Non réalisé" },
      { name: "Organisation des moyens humains pour le chantier", date: "", status: "Non réalisé" },
      { name: "Création de la fiche de chantier", date: "", status: "Non réalisé" },
      { name: "Accusé réception des livraisons", date: "", status: "Non réalisé" },
      { name: "Transmission de la facture de situation N°3", date: "", status: "Non réalisé" },
      { name: "Date de début des travaux", date: "", status: "Non réalisé" },
      { name: "Organisation des visites de suivi et d'inspection", date: "", status: "Non réalisé" },
      { name: "Organisation de la mise en service de l'installation", date: "", status: "Non réalisé" },
      { name: "Réalisation du consuel", date: "", status: "Non réalisé" },
      { name: "Réalisation du PV de réception", date: "", status: "Non réalisé" },
      { name: "Transmission de la facture de solde", date: "", status: "Non réalisé" },
      { name: "Demande d'avis client", date: "", status: "Non réalisé" }
    ];

    return { defaultChargeAffairePhases, defaultChefProjetPhases };
  };

  const calculateProgressPercentage = (phases) => {
    if (!phases || phases.length === 0) return 0;
    
    const completedPhases = phases.filter(phase => 
      phase.status?.toLowerCase() === 'terminé'
    ).length;
    
    return Math.round((completedPhases / phases.length) * 100);
  };

  const getDossierProgress = (dossier) => {
    const { defaultChargeAffairePhases, defaultChefProjetPhases } = getDefaultPhases();
    
    // Utiliser les phases du dossier ou les phases par défaut
    const chargeAffairePhases = dossier.phasesChargeAffaire?.length > 0 
      ? dossier.phasesChargeAffaire 
      : defaultChargeAffairePhases;
      
    const chefProjetPhases = dossier.phasesChefProjet?.length > 0 
      ? dossier.phasesChefProjet 
      : defaultChefProjetPhases;

    const chargeAffaireProgress = calculateProgressPercentage(chargeAffairePhases);
    const chefProjetProgress = calculateProgressPercentage(chefProjetPhases);
    const globalProgress = Math.round((chargeAffaireProgress + chefProjetProgress) / 2);

    return {
      chargeAffaire: chargeAffaireProgress,
      chefProjet: chefProjetProgress,
      global: globalProgress
    };
  };

  const getProgressBarColor = (percentage) => {
    if (percentage >= 80) return 'bg-green-500';
    if (percentage >= 50) return 'bg-yellow-500';
    if (percentage >= 25) return 'bg-orange-500';
    return 'bg-red-500';
  };

  // Fonction pour obtenir le badge de statut
  const getStatusBadge = (dossier) => {
    const status = dossier.status?.toLowerCase() || dossier.statut?.toLowerCase();
    
    // ✅ NOUVELLE LOGIQUE : Détecter automatiquement les dossiers "en cours"
    if (!status || status === '' || status === 'undefined' || status === 'null' || status === 'à démarrer') {
      // Si le dossier a une date de création et plus de 1 jour, il devrait être "en cours"
      if (dossier.createdAt) {
        const daysSinceCreation = Math.floor((new Date() - dossier.createdAt.toDate()) / (1000 * 60 * 60 * 24));
        if (daysSinceCreation > 1) {
          return <span className="inline-flex px-2 py-1 text-xs font-semibold bg-green-100 text-green-800 rounded-full">🟢 En cours</span>;
        }
      }
      return <span className="inline-flex px-2 py-1 text-xs font-semibold bg-yellow-100 text-yellow-800 rounded-full">🟡 Non démarré</span>;
    }
    
    switch (status) {
      case 'en retard':
        return <span className="inline-flex px-2 py-1 text-xs font-semibold bg-red-100 text-red-800 rounded-full">🔴 En retard</span>;
      case 'terminé':
      case 'completed':
        return <span className="inline-flex px-2 py-1 text-xs font-semibold bg-blue-100 text-blue-800 rounded-full">✅ Terminé</span>;
      case 'en cours':
      case 'in_progress':
        return <span className="inline-flex px-2 py-1 text-xs font-semibold bg-green-100 text-green-800 rounded-full">🟢 En cours</span>;
      case 'non démarré':
      case 'not_started':
      case 'à démarrer':
        return <span className="inline-flex px-2 py-1 text-xs font-semibold bg-yellow-100 text-yellow-800 rounded-full">🟡 Non démarré</span>;
      default:
        return <span className="inline-flex px-2 py-1 text-xs font-semibold bg-gray-100 text-gray-800 rounded-full">📋 {status}</span>;
    }
  };

  // Fonction pour obtenir la couleur de bordure selon le statut
  const getBorderColor = (dossier) => {
    const status = dossier.status?.toLowerCase() || dossier.statut?.toLowerCase();
    
    // ✅ MÊME LOGIQUE : Détecter automatiquement les dossiers "en cours"
    if (!status || status === '' || status === 'undefined' || status === 'null' || status === 'à démarrer') {
      if (dossier.createdAt) {
        const daysSinceCreation = Math.floor((new Date() - dossier.createdAt.toDate()) / (1000 * 60 * 60 * 24));
        if (daysSinceCreation > 1) {
          return 'border-green-300'; // 🟢 En cours
        }
      }
      return 'border-yellow-300'; // 🟡 Non démarré
    }
    
    switch (status) {
      case 'en retard':
        return 'border-red-300'; // 🔴 En retard
      case 'terminé':
      case 'completed':
        return 'border-blue-300'; // ✅ Terminé (bleu pour différencier)
      case 'en cours':
      case 'in_progress':
        return 'border-green-300'; // 🟢 En cours
      case 'non démarré':
      case 'not_started':
      case 'à démarrer':
        return 'border-yellow-300'; // 🟡 Non démarré
      default:
        return 'border-gray-300';
    }
  };

  // Filtrer les dossiers selon le terme de recherche
  const filteredDossiers = dossiers.filter(dossier =>
    dossier.clientName?.toLowerCase().includes(searchTerm.toLowerCase()) ||
    dossier.address?.toLowerCase().includes(searchTerm.toLowerCase()) ||
    dossier.id.toLowerCase().includes(searchTerm.toLowerCase())
  );

  if (loading) {
    return (
      <div className="flex justify-center items-center h-64">
        <div className="text-gray-500">Chargement des dossiers...</div>
      </div>
    );
  }

  return (
    <div className="space-y-6">
      <div className="sm:flex sm:items-center sm:justify-between">
        <div>
          <h1 className="text-2xl font-bold text-gray-900">Gestion des Dossiers</h1>
          <p className="mt-2 text-sm text-gray-700">
            Liste de tous les dossiers actifs
          </p>
        </div>
      </div>

      <div className="bg-white shadow rounded-lg">
        <div className="px-4 py-5 sm:p-6">
          {/* Barre de recherche */}
          <div className="mb-6">
            <div className="relative">
              <div className="absolute inset-y-0 left-0 pl-3 flex items-center pointer-events-none">
                <Search className="h-5 w-5 text-gray-400" />
              </div>
              <input
                type="text"
                className="block w-full pl-10 pr-3 py-2 border border-gray-300 rounded-md leading-5 bg-white placeholder-gray-500 focus:outline-none focus:placeholder-gray-400 focus:ring-1 focus:ring-blue-500 focus:border-blue-500"
                placeholder="Rechercher par nom, adresse ou ID..."
                value={searchTerm}
                onChange={(e) => setSearchTerm(e.target.value)}
              />
            </div>
          </div>

          {/* Boutons d'action */}
          <div className="flex flex-wrap gap-3 mb-6">
            <Link
              to="/admin/nouveau-dossier"
              className="flex items-center px-4 py-2 bg-green-600 text-white rounded-lg hover:bg-green-700"
            >
              <Plus className="w-4 h-4 mr-2" />
              Nouveau dossier
            </Link>
          </div>

          {/* Statistiques */}
          <div className="grid grid-cols-1 md:grid-cols-3 gap-4 mb-6">
            <div className="bg-blue-50 rounded-lg p-4">
              <div className="text-blue-600 text-sm font-medium">Total</div>
              <div className="text-2xl font-bold text-blue-900">{filteredDossiers.length}</div>
            </div>
            <div className="bg-red-50 rounded-lg p-4">
              <div className="text-red-600 text-sm font-medium">🔴 En retard</div>
              <div className="text-2xl font-bold text-red-900">
                {filteredDossiers.filter(d => (d.status || d.statut) === 'en retard').length}
              </div>
            </div>
            <div className="bg-green-50 rounded-lg p-4">
              <div className="text-green-600 text-sm font-medium">🟢 En cours</div>
              <div className="text-2xl font-bold text-green-900">
                {filteredDossiers.filter(d => {
                  const status = d.status?.toLowerCase() || d.statut?.toLowerCase();
                  if (status === 'en cours' || status === 'in_progress') return true;
                  // Auto-détection : pas de statut mais créé depuis plus d'1 jour
                  if (!status || status === 'à démarrer') {
                    if (d.createdAt) {
                      const daysSinceCreation = Math.floor((new Date() - d.createdAt.toDate()) / (1000 * 60 * 60 * 24));
                      return daysSinceCreation > 1;
                    }
                  }
                  return false;
                }).length}
              </div>
            </div>
          </div>

          {/* Liste des dossiers */}
          <div className="grid gap-6 sm:grid-cols-1 lg:grid-cols-2">
            {filteredDossiers.length === 0 ? (
              <div className="col-span-2 text-center py-12">
                <div className="text-gray-500">Aucun dossier trouvé</div>
              </div>
            ) : (
              filteredDossiers.map((dossier) => {
                const progress = getDossierProgress(dossier);
                
                return (
                  <div 
                    key={dossier.id} 
                    className={`bg-white border-2 ${getBorderColor(dossier)} rounded-lg p-4 shadow-sm hover:shadow-md transition-shadow`}
                  >
                    {/* Header avec statut */}
                    <div className="flex justify-between items-start mb-3">
                      <div className="flex-1">
                        <h3 className="text-base font-semibold text-gray-900">
                          {dossier.clientName || 'Client non défini'}
                        </h3>
                        <p className="text-xs text-gray-500">ID: {dossier.id}</p>
                      </div>
                      {getStatusBadge(dossier)}
                    </div>

                    {/* Informations du dossier */}
                    <div className="space-y-1 mb-3">
                      <div className="text-xs">
                        <span className="font-medium text-gray-700">Adresse:</span>
                        <span className="ml-2 text-gray-600">
                          {dossier.address || 'Non renseignée'}
                        </span>
                      </div>
                      <div className="text-xs">
                        <span className="font-medium text-gray-700">Téléphone:</span>
                        <span className="ml-2 text-gray-600">
                          {dossier.phone || 'Non renseigné'}
                        </span>
                      </div>
                      <div className="text-xs">
                        <span className="font-medium text-gray-700">Créé le:</span>
                        <span className="ml-2 text-gray-600">
                          {dossier.createdAt ? new Date(dossier.createdAt.toDate()).toLocaleDateString() : 'Date inconnue'}
                        </span>
                      </div>
                    </div>

                    {/* Barres de progression */}
                    <div className="mb-3 space-y-2">
                      {/* Timeline Chargé d'affaire */}
                      <div>
                        <div className="flex justify-between items-center mb-1">
                          <span className="text-xs text-gray-600">Chargé d'affaire</span>
                          <span className="text-xs text-gray-500">{progress.chargeAffaire}%</span>
                        </div>
                        <div className="w-full bg-gray-200 rounded-full h-1.5">
                          <div 
                            className={`h-1.5 rounded-full transition-all duration-300 ${getProgressBarColor(progress.chargeAffaire)}`}
                            style={{ width: `${progress.chargeAffaire}%` }}
                          ></div>
                        </div>
                      </div>

                      {/* Timeline Chef de projet */}
                      <div>
                        <div className="flex justify-between items-center mb-1">
                          <span className="text-xs text-gray-600">Chef de projet</span>
                          <span className="text-xs text-gray-500">{progress.chefProjet}%</span>
                        </div>
                        <div className="w-full bg-gray-200 rounded-full h-1.5">
                          <div 
                            className={`h-1.5 rounded-full transition-all duration-300 ${getProgressBarColor(progress.chefProjet)}`}
                            style={{ width: `${progress.chefProjet}%` }}
                          ></div>
                        </div>
                      </div>

                      {/* Avancement global - Plus distinctif */}
                      <div className="border-t border-gray-200 pt-2 mt-2">
                        <div className="flex justify-between items-center mb-1">
                          <span className="text-xs font-semibold text-gray-800">Avancement global</span>
                          <span className="text-xs font-bold text-gray-900 bg-gray-100 px-1.5 py-0.5 rounded text-xs">{progress.global}%</span>
                        </div>
                        <div className="w-full bg-gray-300 rounded-full h-2.5 shadow-inner">
                          <div 
                            className={`h-2.5 rounded-full transition-all duration-500 shadow-sm ${getProgressBarColor(progress.global)} 
                              ${progress.global > 0 ? 'bg-gradient-to-r from-current to-opacity-80' : ''}`}
                            style={{ width: `${progress.global}%` }}
                          ></div>
                        </div>
                      </div>
                    </div>

                    {/* Boutons d'action */}
                    <div className="flex flex-wrap gap-1.5">
                      <Link
                        to={`/admin/dossier/${dossier.id}`}
                        className="inline-flex items-center px-2 py-1 border border-gray-300 shadow-sm text-xs leading-3 font-medium rounded text-gray-700 bg-white hover:bg-gray-50 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-blue-500"
                      >
                        <Eye className="w-3 h-3 mr-1" />
                        Détails
                      </Link>

                      <Link
                        to={`/admin/dossier/${dossier.id}/edit`}
                        className="inline-flex items-center px-2 py-1 border border-gray-300 shadow-sm text-xs leading-3 font-medium rounded text-gray-700 bg-white hover:bg-gray-50 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-blue-500"
                      >
                        <Edit className="w-3 h-3 mr-1" />
                        Modifier
                      </Link>

                      <button
                        onClick={() => handleCloseDossier(dossier.id, dossier.clientName)}
                        className="inline-flex items-center px-2 py-1 border border-blue-300 shadow-sm text-xs leading-3 font-medium rounded text-blue-700 bg-blue-50 hover:bg-blue-100 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-blue-500"
                      >
                        <Lock className="w-3 h-3 mr-1" />
                        Clôturer
                      </button>

                      <button
                        onClick={() => handleDelete(dossier.id)}
                        className="inline-flex items-center px-2 py-1 border border-red-300 shadow-sm text-xs leading-3 font-medium rounded text-red-700 bg-red-50 hover:bg-red-100 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-red-500"
                      >
                        <Trash2 className="w-3 h-3 mr-1" />
                        Supprimer
                      </button>
                    </div>
                  </div>
                );
              })
            )}
          </div>
        </div>
      </div>
    </div>
  );
};

export default DossiersList;